import type { ErrorEvent, EventHint } from '@sentry/vue';
import * as Sentry from '@sentry/vue';
import type { App } from 'vue';

import type { Locale } from '@/config/build';
import { applyMiddleware, filterMFError, ignoreChunkErrors } from '@/shared/sentry/middlewares';
import { MF_DSN_KEY, mfSentryTransport } from '@/shared/sentry/transports';

interface SentryConfig {
  dsn: string;
  environment: string;
  locale: Locale;
}

interface SentryScopeConfig {
  locale: Locale;
  testingName?: string;
}

interface SentryUserConfig {
  id: string;
  userType: string;
  facilities: string[];
}

function initSentry(app: App, config: SentryConfig) {
  Sentry.init({
    app,
    dsn: config.dsn,
    environment: config.environment,
    transport: mfSentryTransport,
    integrations: [Sentry.moduleMetadataIntegration(), Sentry.extraErrorDataIntegration(), Sentry.dedupeIntegration()],
    beforeSend: (event: ErrorEvent, hint: EventHint) => {
      return applyMiddleware([
        {
          middleware: () => filterMFError(event),
          priority: 100,
          shouldTerminate: (result) => !!(result?.extra && result.extra[MF_DSN_KEY]),
        },
        {
          middleware: () => ignoreChunkErrors(event, hint),
          priority: 10,
        },
      ]);
    },
  });
}

function configureSentryScope({ locale, testingName }: SentryScopeConfig): void {
  const currentScope = Sentry.getCurrentScope();

  if (testingName) {
    currentScope.setTag('testing_name', testingName);
  }

  currentScope.setTag('locale', locale);
}

function setUser({ id, userType, facilities }: SentryUserConfig) {
  Sentry.setUser({
    id,
    userType,
    facilities,
  });
}

function captureException(message: string, extra?: Record<string, unknown>) {
  Sentry.captureException(new Error(message), {
    extra,
  });
}

export { captureException, configureSentryScope, initSentry, setUser };
